import { ContentCopy, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  CheckboxGroupInput,
  required,
  SaveButton,
  SimpleForm,
  SimpleShowLayout,
  useDataProvider,
  useLocaleState,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from "react-admin";
import { FieldValues, useFormState } from "react-hook-form";

import { PasswordResetRecipient } from "../generated/graphql";
import { ACCESS_STATUS, AccessStatus, getAccessStatus } from "../lib/accessStatus";
import { formatDateTimeInline } from "../lib/dateUtils";
import { space } from "../theme/designTokens";
import { dialogContentSx } from "../theme/dialogStyles";
import { AccessStatusChip } from "./fields/AccessStatusChip";
import { PractitionerRecord } from "./PractitionerShow";

interface FormData extends FieldValues {
  userId?: string;
  recipients?: PasswordResetRecipient[];
}

const SubmitButton = ({ label }: { label: string }) => {
  const { isValid } = useFormState();

  return (
    <SaveButton
      icon={<></>}
      type="submit"
      label={label}
      color="primary"
      disabled={!isValid}
      data-testid="password-reset-submit"
      sx={{
        height: space.xxxl,
        alignSelf: "flex-start",
      }}
    />
  );
};

const contentByAccessStatus: Record<
  Exclude<AccessStatus, typeof ACCESS_STATUS.Deactivated>,
  { title: string; description: string; submitLabel?: string }
> = {
  [ACCESS_STATUS.Active]: {
    title: "passwordManagement.titleReset",
    description: "passwordManagement.descriptionReset",
    submitLabel: "passwordManagement.sendButton",
  },
  [ACCESS_STATUS.Blocked]: {
    title: "passwordManagement.titleBlocked",
    description: "passwordManagement.descriptionBlocked",
    submitLabel: "passwordManagement.sendButton",
  },
  [ACCESS_STATUS.OtpPending]: {
    title: "passwordManagement.titleOtpPending",
    description: "passwordManagement.descriptionOtpPending",
  },
  [ACCESS_STATUS.NoAccess]: {
    title: "passwordManagement.titleCreate",
    description: "passwordManagement.descriptionCreate",
    submitLabel: "passwordManagement.sendButton",
  },
};

export const PasswordManagementTab = () => {
  const [revealedOtp, setRevealedOtp] = useState<string | null>(null);
  const [isRevealDialogOpen, setIsRevealDialogOpen] = useState(false);
  const [managerPassword, setManagerPassword] = useState("");
  const [isRevealing, setIsRevealing] = useState(false);
  const record = useRecordContext<PractitionerRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [locale] = useLocaleState();

  const clearReveal = useCallback(() => {
    setRevealedOtp(null);
    setManagerPassword("");
    setIsRevealDialogOpen(false);
  }, []);

  useEffect(() => clearReveal, [clearReveal]);

  useEffect(() => {
    clearReveal();
  }, [clearReveal, record?.authStatus, record?.authStatusValidUntil, record?.status, record?.userId]);

  if (!record) return null;

  const accessStatus = getAccessStatus(record.status, record.authStatus);
  const validUntil = record.authStatusValidUntil ? formatDateTimeInline(record.authStatusValidUntil, locale) : null;

  const resetOptions = [
    ...(record.email ? [{ id: PasswordResetRecipient.User, name: record.firstName }] : []),
    { id: PasswordResetRecipient.Admin, name: translate("passwordManagement.admin") },
  ];

  const handleCopyOtp = () => {
    if (!revealedOtp) return;

    navigator.clipboard.writeText(revealedOtp);
    notify("passwordManagement.copySuccess", { type: "success" });
  };

  const closeRevealDialog = () => {
    setManagerPassword("");
    setIsRevealDialogOpen(false);
  };

  const revealOtp = async () => {
    if (!record.userId) return;

    setIsRevealing(true);
    try {
      const result = await dataProvider.custom("Practitioner", "revealPasswordOTP", {
        userId: record.userId,
        password: managerPassword,
      });
      setManagerPassword("");
      setIsRevealDialogOpen(false);
      if (result.status !== "REVEALED" || !result.otp) {
        setRevealedOtp(null);
        notify("passwordManagement.revealError", { type: "error" });
        return;
      }

      setRevealedOtp(result.otp);
    } catch {
      clearReveal();
      notify("passwordManagement.revealError", { type: "error" });
    } finally {
      setIsRevealing(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      clearReveal();
      const recipients = data.recipients || [];

      const recipient = recipients.length === 2 ? PasswordResetRecipient.Both : recipients[0];

      const { warningCode } = await dataProvider.custom("Practitioner", "resetPasswordOTP", {
        userId: data.userId,
        recipient,
      });
      if (warningCode === "USER_HAS_NO_EMAIL") {
        notify("passwordManagement.noEmailWarning", {
          type: "warning",
        });
      } else {
        notify("passwordManagement.resetSuccess", { type: "success" });
      }
      refresh();
    } catch {
      notify("passwordManagement.resetError", { type: "error" });
    }
  };

  const statusHeader = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: space.xs, mb: space.sm }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: space.xs }}>
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          {translate("passwordManagement.statusLabel")}:
        </Typography>
        <AccessStatusChip accessStatus={accessStatus} />
      </Box>
      {accessStatus !== ACCESS_STATUS.Deactivated && validUntil && (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: space.xs }}>
          <Typography
            variant="body2"
            data-testid={accessStatus === ACCESS_STATUS.Active ? "reset-pending-hint" : "otp-valid-until"}
          >
            {translate(
              accessStatus === ACCESS_STATUS.Active
                ? "passwordManagement.resetPendingHint"
                : "passwordManagement.otpValidUntil",
              { date: validUntil }
            )}
          </Typography>
          <Box
            role="group"
            aria-label={translate("passwordManagement.otpLabel")}
            sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: space.xs }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: space.xxs, minHeight: "36px" }}>
              <Typography
                component="span"
                variant="body2"
                data-testid={revealedOtp ? "revealed-otp" : "masked-otp"}
                aria-live="polite"
                sx={{ minWidth: "6ch", fontFamily: "monospace", fontWeight: "bold", letterSpacing: "0.12em" }}
              >
                {revealedOtp ?? translate("passwordManagement.maskedOtp")}
              </Typography>
              {revealedOtp && (
                <IconButton
                  aria-label={translate("passwordManagement.copyButton")}
                  onClick={handleCopyOtp}
                  size="small"
                >
                  <ContentCopy />
                </IconButton>
              )}
            </Box>
            <Button
              onClick={() => (revealedOtp ? clearReveal() : setIsRevealDialogOpen(true))}
              size="small"
              startIcon={revealedOtp ? <VisibilityOff /> : <Visibility />}
            >
              {translate(revealedOtp ? "passwordManagement.hideButton" : "passwordManagement.revealButton")}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  if (accessStatus === ACCESS_STATUS.Deactivated) {
    return (
      <SimpleShowLayout>
        {statusHeader}
        <Typography variant="body1" data-testid="deactivated-hint">
          {translate("passwordManagement.deactivatedHint")}
        </Typography>
      </SimpleShowLayout>
    );
  }

  const content = contentByAccessStatus[accessStatus];
  const title = translate(content.title);
  const description = translate(content.description, {
    name: record.firstName,
    date: validUntil ?? "-",
  });
  const submitLabel = content.submitLabel ? translate(content.submitLabel) : null;

  return (
    <SimpleShowLayout>
      {statusHeader}
      <Box sx={{ mb: submitLabel ? 0 : space.sm, display: "flex", flexDirection: "column" }}>
        <Typography variant="h6" my={space.xs}>
          {title}
        </Typography>
        <Typography variant="body1">{description}</Typography>
      </Box>
      {submitLabel && (
        <SimpleForm toolbar={false} onSubmit={onSubmit} sx={{ p: 0 }}>
          <Box sx={{ mb: space.sm, display: "flex", flexDirection: "column" }}>
            {!record.email && (
              <Typography variant="body2" sx={{ mt: space.xs }} data-testid="no-email-hint">
                {translate("passwordManagement.noEmailHint", { name: record.firstName })}
              </Typography>
            )}
            {/* Workaround: react-admin's CheckboxGroupInput documentation mentions support for disabling individual choices via the 'disabled' property
              (https://marmelab.com/react-admin/CheckboxGroupInput.html#disablevalue), but it doesn't actually work in practice.
              When the user has no email, we show a disabled checkbox for visualization and exclude the option from CheckboxGroupInput choices. */}
            <Box sx={{ ml: space.xs, display: "flex", flexDirection: "row", my: space.xs }}>
              {!record.email && (
                <FormControlLabel
                  // Using hardcoded 3px instead of theme spacing to vertically center MUI checkbox with react-admin CheckboxGroupInput on the same line
                  sx={{ marginTop: "3px", marginRight: space.sm }}
                  control={<Checkbox disabled />}
                  label={record.firstName}
                />
              )}
              <CheckboxGroupInput
                label={false}
                source="recipients"
                choices={resetOptions}
                row
                validate={required()}
                sx={{
                  "& .MuiFormGroup-root": { gap: space.sm },
                  "& .MuiFormHelperText-root": { display: "none" },
                }}
              />
            </Box>
            <SubmitButton label={submitLabel} />
          </Box>
        </SimpleForm>
      )}
      <Dialog open={isRevealDialogOpen} onClose={closeRevealDialog} fullWidth>
        <Box
          component="form"
          onSubmit={event => {
            event.preventDefault();
            revealOtp();
          }}
        >
          <DialogTitle>{translate("passwordManagement.revealDialogTitle")}</DialogTitle>
          <DialogContent data-testid="otp-reveal-dialog-content">
            <Box sx={{ ...dialogContentSx, pb: 0 }}>
              <TextField
                autoFocus
                fullWidth
                label={translate("passwordManagement.revealPasswordLabel")}
                onChange={event => setManagerPassword(event.target.value)}
                required
                type="password"
                value={managerPassword}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRevealDialog}>{translate("ra.action.cancel")}</Button>
            <Button disabled={!managerPassword || isRevealing} type="submit" variant="contained">
              {translate("passwordManagement.revealConfirmButton")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </SimpleShowLayout>
  );
};
